/**
 * Hero Search Web Component
 * A reusable search component for filtering helpful topics and managing section visibility
 */
class HeroSearch extends HTMLElement {
  constructor() {
    super();

    // Component configuration with default values
    this.config = {
      debounce: parseInt(this.getAttribute('data-debounce')) || 150,
      minLength: parseInt(this.getAttribute('data-min-length')) || 1,
      highlight: this.getAttribute('data-highlight') === 'true',
      announce: this.getAttribute('data-announce') === 'true',
      keyboard: this.getAttribute('data-keyboard') === 'true',
    };

    // Component state
    this.state = {
      searchTerm: '',
      isSearching: false,
      originalDisplayValues: new Map(),
      noResultsMessage: null,
    };

    // Bind methods to maintain context
    this.performSearch = this.performSearch.bind(this);
    this.handleInput = this.handleInput.bind(this);
    this.handleKeydown = this.handleKeydown.bind(this);
  }

  connectedCallback() {
    this.initializeComponent();
  }

  disconnectedCallback() {
    this.cleanup();
  }

  /**
   * Initialize the component
   */
  initializeComponent() {
    const heroSearchInput = this.querySelector('[data-hero-search-input]');

    if (!heroSearchInput) {
      console.warn('HeroSearch: No search input found');
      return;
    }

    // Find target elements
    this.elements = {
      input: heroSearchInput,
      helpfulTopicsGrid: document.querySelector('#helpful-topics-grid'),
      helpfulItems: document.querySelectorAll('.helpful-item'),
      allSections: this.getAllSections(),
    };

    if (!this.elements.helpfulTopicsGrid || this.elements.helpfulItems.length === 0) {
      console.warn('HeroSearch: Target elements not found');
      return;
    }

    // Store original display values
    this.storeOriginalDisplayValues();

    // Create no results message
    this.createNoResultsMessage();

    // Setup event listeners
    this.setupEventListeners();

    // Announce component initialization
    if (this.config.announce) {
      this.announce('Search component initialized');
    }
  }

  /**
   * Get all sections that should be managed by the search
   */
  getAllSections() {
    const mainContent = document.querySelector(
      '.MainContent, #MainContent, [class*="MainContent"], [id*="MainContent"]'
    );

    return mainContent
      ? mainContent.querySelectorAll(
          'section:not(.hero-search-section), .shopify-section:not(.hero-search-section)'
        )
      : document.querySelectorAll(
          'section:not(.hero-search-section), .shopify-section:not(.hero-search-section)'
        );
  }

  /**
   * Store original display values for sections
   */
  storeOriginalDisplayValues() {
    this.elements.allSections.forEach((section) => {
      this.state.originalDisplayValues.set(section, section.style.display || '');
    });
  }

  /**
   * Create and insert no results message
   */
  createNoResultsMessage() {
    if (this.state.noResultsMessage) return;

    this.state.noResultsMessage = document.createElement('div');
    this.state.noResultsMessage.id = 'no-results-message';
    this.state.noResultsMessage.className = 'no-results-message';
    this.state.noResultsMessage.style.display = 'none';
    this.state.noResultsMessage.innerHTML =
      '<p>No topics found matching your search. Try a different search term.</p>';

    this.elements.helpfulTopicsGrid.parentNode.insertBefore(
      this.state.noResultsMessage,
      this.elements.helpfulTopicsGrid.nextSibling
    );
  }

  /**
   * Setup event listeners
   */
  setupEventListeners() {
    // Input event with debouncing
    this.elements.input.addEventListener('input', this.handleInput);

    // Keyboard navigation
    if (this.config.keyboard) {
      this.elements.input.addEventListener('keydown', this.handleKeydown);
    }
  }

  /**
   * Handle input events with debouncing
   */
  handleInput(event) {
    const searchTerm = event.target.value;

    // Clear existing timeout
    if (this.debounceTimeout) {
      clearTimeout(this.debounceTimeout);
    }

    // Set new timeout
    this.debounceTimeout = setTimeout(() => {
      this.performSearch(searchTerm);
    }, this.config.debounce);
  }

  /**
   * Handle keyboard events
   */
  handleKeydown(event) {
    if (event.key === 'Enter') {
      event.preventDefault();
      this.performSearch(event.target.value);
    }
  }

  /**
   * Perform the search operation
   */
  performSearch(searchTerm) {
    this.state.searchTerm = searchTerm;
    this.state.isSearching = searchTerm.trim() !== '';

    let visibleCount = 0;

    // Handle section visibility
    this.handleSectionVisibility(searchTerm);

    // Handle helpful topics filtering
    visibleCount = this.filterHelpfulTopics(searchTerm);

    // Show/hide no results message
    this.toggleNoResultsMessage(visibleCount, searchTerm);

    // Announce results if enabled
    if (this.config.announce) {
      this.announceSearchResults(visibleCount, searchTerm);
    }
  }

  /**
   * Handle section visibility based on search term
   */
  handleSectionVisibility(searchTerm) {
    if (searchTerm.trim() !== '') {
      // Hide content sections except helpful topics and newsletter
      this.elements.allSections.forEach((section) => {
        const isHelpfulTopics = this.isHelpfulTopicsSection(section);
        const isNewsletter = this.isNewsletterSection(section);
        const isHeroSearch = this.isHeroSearchSection(section);

        if (!isHelpfulTopics && !isNewsletter && !isHeroSearch) {
          section.style.display = 'none';
        }
      });
    } else {
      // Show all sections when search is empty
      this.elements.allSections.forEach((section) => {
        const originalDisplay = this.state.originalDisplayValues.get(section);
        section.style.display = originalDisplay;
      });
    }
  }

  /**
   * Check if section is helpful topics section
   */
  isHelpfulTopicsSection(section) {
    return (
      section.classList.contains('helpful-topics') ||
      section.querySelector('.helpful-topics') ||
      section.id === 'helpful-topics-grid' ||
      section.querySelector('#helpful-topics-grid')
    );
  }

  /**
   * Check if section is newsletter section
   */
  isNewsletterSection(section) {
    return (
      section.id === 'shopify-section-newsletter-section' ||
      section.classList.contains('shopify-section-newsletter-section') ||
      section.querySelector('#shopify-section-newsletter-section')
    );
  }

  /**
   * Check if section is hero search section
   */
  isHeroSearchSection(section) {
    return (
      section.classList.contains('hero-search-section') ||
      section.querySelector('.hero-search-section') ||
      section.closest('.hero-search-section')
    );
  }

  /**
   * Filter helpful topics based on search term
   */
  filterHelpfulTopics(searchTerm) {
    let visibleCount = 0;

    this.elements.helpfulItems.forEach((item) => {
      const topicTitle = item.getAttribute('data-topic-title') || '';
      const topicLabel = item.querySelector('span')?.textContent || '';
      const searchText = (topicTitle + ' ' + topicLabel).toLowerCase();

      if (searchTerm === '' || searchText.includes(searchTerm.toLowerCase())) {
        item.style.display = 'block';
        item.classList.remove('hidden');
        item.setAttribute('aria-hidden', 'false');
        visibleCount++;
      } else {
        item.style.display = 'none';
        item.classList.add('hidden');
        item.setAttribute('aria-hidden', 'true');
      }
    });

    return visibleCount;
  }

  /**
   * Toggle no results message visibility
   */
  toggleNoResultsMessage(visibleCount, searchTerm) {
    if (this.state.noResultsMessage) {
      this.state.noResultsMessage.style.display =
        visibleCount === 0 && searchTerm !== '' ? 'block' : 'none';
    }
  }

  /**
   * Announce search results for accessibility
   */
  announceSearchResults(visibleCount, searchTerm) {
    if (searchTerm === '') {
      this.announce('All topics shown');
    } else if (visibleCount === 0) {
      this.announce('No topics found matching your search');
    } else {
      this.announce(`${visibleCount} topics found`);
    }
  }

  /**
   * Announce message for screen readers
   */
  announce(message) {
    const announcement = document.createElement('div');
    announcement.setAttribute('aria-live', 'polite');
    announcement.setAttribute('aria-atomic', 'true');
    announcement.style.position = 'absolute';
    announcement.style.left = '-10000px';
    announcement.style.width = '1px';
    announcement.style.height = '1px';
    announcement.style.overflow = 'hidden';
    announcement.textContent = message;

    document.body.appendChild(announcement);

    setTimeout(() => {
      document.body.removeChild(announcement);
    }, 1000);
  }

  /**
   * Cleanup component
   */
  cleanup() {
    if (this.debounceTimeout) {
      clearTimeout(this.debounceTimeout);
    }

    if (this.elements && this.elements.input) {
      this.elements.input.removeEventListener('input', this.handleInput);
      this.elements.input.removeEventListener('keydown', this.handleKeydown);
    }
  }

  /**
   * Public method to get current search term
   */
  getSearchTerm() {
    return this.state.searchTerm;
  }

  /**
   * Public method to clear search
   */
  clearSearch() {
    if (this.elements && this.elements.input) {
      this.elements.input.value = '';
      this.performSearch('');
    }
  }

  /**
   * Public method to set search term
   */
  setSearchTerm(term) {
    if (this.elements && this.elements.input) {
      this.elements.input.value = term;
      this.performSearch(term);
    }
  }
}

// Register the custom element
document.addEventListener('DOMContentLoaded', () => {
  if (!customElements.get('hero-search')) {
    customElements.define('hero-search', HeroSearch);
  }
});
